/**
 * Fetch helpers shared by every loader in `src/data/`.
 *
 * `NotPublishedYetError` is the one signal every panel component treats as
 * a real, styled state (`src/panels/NotPublishedYet.ts`) rather than a
 * crash — a 404 (or a network error indistinguishable from "this file does
 * not exist on a static host") on any of the optional snapshot paths just
 * means the producing lane has not shipped that artifact yet (OBS49 rule 1:
 * "never crash, never fabricate").
 *
 * A 403 is treated identically to a 404. Verified live against the real
 * host (2026-09-26): a `refs.*`-addressed object (`index.json`'s
 * `equity_curve`/`performance`/`life`) sometimes answers 403 rather than
 * 404 for content that is not (yet) actually readable — indistinguishable,
 * from a public read-only static client's point of view, from "not
 * published yet"; there is no legitimate reason this site would ever
 * intentionally return an access-denied response to a real request.
 */

export class NotPublishedYetError extends Error {
  constructor(public readonly url: string) {
    super(`not published yet: ${url}`);
    this.name = "NotPublishedYetError";
  }
}

export class SnapshotFetchError extends Error {
  constructor(
    public readonly url: string,
    public readonly status: number | null,
    cause?: unknown,
  ) {
    super(`failed to fetch ${url}${status !== null ? ` (status ${status})` : ""}`);
    this.name = "SnapshotFetchError";
    this.cause = cause;
  }
}

const DEFAULT_TIMEOUT_MS = 10_000;

/** Fetches raw bytes, translating a missing static file into
 * `NotPublishedYetError` and any other failure into `SnapshotFetchError`. */
export async function fetchBytes(
  url: string,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<ArrayBuffer> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal, cache: "no-store" });
    if (response.status === 404 || response.status === 403) {
      throw new NotPublishedYetError(url);
    }
    if (!response.ok) {
      throw new SnapshotFetchError(url, response.status);
    }
    return await response.arrayBuffer();
  } catch (error) {
    if (error instanceof NotPublishedYetError || error instanceof SnapshotFetchError) throw error;
    throw new SnapshotFetchError(url, null, error);
  } finally {
    clearTimeout(timer);
  }
}

/** Fetches and parses JSON, typed by the caller (this module does no schema
 * validation itself — each loader in `src/data/loaders/` narrows its own
 * shape so a malformed file fails at the call site that knows what "valid"
 * means for it). */
export async function fetchJson<T>(url: string, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<T> {
  const bytes = await fetchBytes(url, timeoutMs);
  const text = new TextDecoder("utf-8").decode(bytes);
  try {
    return JSON.parse(text) as T;
  } catch (error) {
    throw new SnapshotFetchError(url, null, error);
  }
}

/** True when `error` means "this optional artifact has not been published
 * yet" as opposed to a genuine transport failure worth surfacing loudly. */
export function isNotPublishedYet(error: unknown): error is NotPublishedYetError {
  return error instanceof NotPublishedYetError;
}
